// src/components/Organizers.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    Ticket,
    ShieldCheck,
    Zap,
    Users,
    Star,
    Smartphone,
    CreditCard,
    BarChart3,
    ChevronRight,
    LogIn,
    LayoutDashboard,
    ChevronDown,
    X,
    Menu
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { loadUser, logout } from "../store/slices/authSlice";
import axios from "../utils/axiosConfig";
import { buildApiUrl, showMissingApiUrlAlert } from "../utils/apiConfig";
import CreateSalonModal from "./landing/CreateSalonModal";
import ClaimSalonModal from "./landing/ClaimSalonModal";
import PublicFooter from "./layout/PublicFooter";

const Organizers = () => {
    const [openCreate, setOpenCreate] = useState(null); // { billingModel, organizationType, hideSelectors }
    const [success, setSuccess] = useState({ open: false, slug: null, claimUrl: null });
    const [mySalons, setMySalons] = useState([]);
    const [loadingSalons, setLoadingSalons] = useState(false);
    const [showPicker, setShowPicker] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isAuthenticated, loading: authLoading } = useSelector((s) => s.auth);

    useEffect(() => {
        dispatch(loadUser());
    }, [dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchMySalons();
        }
    }, [isAuthenticated]);

    const fetchMySalons = async () => {
        try {
            setLoadingSalons(true);
            const { data } = await axios.get("/api/auth/my-salons");
            setMySalons(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Error fetching salons:", err);
            setMySalons([]);
        } finally {
            setLoadingSalons(false);
        }
    };

    const openCreateModal = (billingModel = "subscription", organizationType = "events", hideSelectors = false) => {
        setSuccess({ open: false, slug: null, claimUrl: null });
        setOpenCreate({ billingModel, organizationType, hideSelectors });
    };

    const handleCreated = ({ slug: newSlug, claimUrl } = {}) => {
        setOpenCreate(null);
        if (!newSlug) return;
        setSuccess({ open: true, slug: newSlug, claimUrl: claimUrl || null });
    };

    const handleLoginClick = () => {
        if (!isAuthenticated) {
            const loginUrl = buildApiUrl('/api/auth/google');
            if (!loginUrl) {
                showMissingApiUrlAlert();
                return;
            }
            window.location.href = loginUrl;
            return;
        }
        if (mySalons.length === 0) {
            openCreateModal('subscription', 'venue', false);
            return;
        }
        if (mySalons.length > 0) {
            setShowPicker(true);
        }
    };

    const handleLogout = async () => {
        try {
            await dispatch(logout()).unwrap();
            setMySalons([]);
        } catch (err) {
            console.error("Error logging out:", err);
        }
    };

    return (
        <div className="min-h-screen bg-[#080808] text-white selection:bg-primary-orange/30">
            <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary-orange/10 rounded-full blur-[120px] animate-glow" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary-red/10 rounded-full blur-[120px] animate-glow" style={{ animationDelay: '2s' }} />
            </div>

            {/* NAVBAR */}
            <header className="fixed top-0 inset-x-0 z-50 bg-[#080808]/60 backdrop-blur-xl border-b border-white/5">
                <nav className="mx-auto max-w-7xl px-6 h-20 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2 group">
                        <div className="w-10 h-10 bg-brand-gradient rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/20 group-hover:scale-110 transition-transform">
                            <Ticket className="text-white" size={20} />
                        </div>
                        <span className="font-black tracking-tighter text-2xl">PASSAR</span>
                    </Link>

                    <div className="flex items-center gap-3 md:gap-6">
                        <div className="hidden sm:flex items-center gap-6">
                            <Link to="/eventos" className="text-sm font-bold text-white/40 hover:text-white transition-colors">Eventos</Link>
                            <a href="/suport" className="text-sm font-bold text-white/40 hover:text-white transition-colors">Soporte</a>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleLoginClick}
                                disabled={authLoading || loadingSalons}
                                className="px-4 md:px-6 py-2 md:py-2.5 bg-white/5 border border-white/10 rounded-full text-[10px] md:text-sm font-bold hover:bg-white/10 text-white transition-all flex items-center gap-2"
                            >
                                {authLoading || loadingSalons ? (
                                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <>
                                        <LogIn size={16} className="text-primary-orange" />
                                        <span>Panel Control</span>
                                    </>
                                )}
                            </button>

                            <button
                                className="sm:hidden p-2 -mr-2 text-white/40 hover:text-white transition-colors"
                                onClick={() => setIsMobileMenuOpen(true)}
                            >
                                <Menu size={24} />
                            </button>
                        </div>
                    </div>
                </nav>
            </header>

            {/* HERO SECTION */}
            <section className="relative pt-40 pb-20 lg:pt-52 lg:pb-32 px-6 z-10 text-center lg:text-left">
                <div className="mx-auto max-w-7xl grid lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-7">
                        <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black leading-[0.9] tracking-tight mb-8 animate-fade-in-up">
                            Gestioná tu negocio <br />
                            y eventos <span className="text-gradient">sin vueltas.</span>
                        </h1>
                        <p className="text-lg sm:text-xl text-white/40 font-medium max-w-2xl mb-12 animate-fade-in-up">
                            La plataforma definitiva para <strong className="text-white">salones, productoras y complejos deportivos</strong>. Vendé tickets o torneos, cobrá al instante y controlá todo tu negocio con un click.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={() => openCreateModal('subscription', 'venue', false)}
                                className="bg-brand-gradient px-10 py-5 rounded-[24px] font-black text-xl text-white shadow-xl shadow-orange-500/20 hover:scale-[1.05] transition-all"
                            >
                                CREAR SALÓN / PRODUCTORA
                            </button>
                            <button
                                onClick={handleLoginClick}
                                className="bg-white/5 border border-white/10 px-10 py-5 rounded-[24px] font-black text-xl text-white hover:bg-white/10 transition-all"
                            >
                                INGRESAR AL PANEL
                            </button>
                        </div>
                    </div>
                    <div className="lg:col-span-5 hidden lg:block relative text-center">
                        {/* FLOTANTE: COBRO RECIBIDO */}
                        <div className="absolute -left-12 top-32 z-20 bg-[#1A110B]/90 backdrop-blur-md border border-primary-orange/20 p-4 rounded-2xl shadow-xl shadow-orange-500/10 animate-float-delayed flex items-center gap-4">
                            <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                                <Zap size={20} className="text-green-500" />
                            </div>
                            <div className="text-left">
                                <p className="text-[10px] uppercase font-black tracking-widest text-primary-orange/60 mb-1">COBRO RECIBIDO</p>
                                <p className="text-2xl font-black text-white leading-none">$45.200,00</p>
                            </div>
                        </div>

                        {/* FLOTANTE: TICKETS VENDIDOS */}
                        <div className="absolute -right-8 bottom-40 z-20 bg-[#1A110B]/90 backdrop-blur-md border border-primary-orange/20 p-4 rounded-2xl shadow-xl shadow-orange-500/10 animate-float flex items-center gap-4">
                            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                                <Users size={20} className="text-blue-500" />
                            </div>
                            <div className="text-left">
                                <p className="text-[10px] uppercase font-black tracking-widest text-primary-orange/60 mb-1">SOLD TICKETS</p>
                                <p className="text-xl font-black text-white leading-none">84% Cap</p>
                            </div>
                        </div>

                        {/* EL TELÉFONO */}
                        <div className="relative mx-auto w-[280px] h-[560px] bg-[#121212] rounded-[52px] border-[12px] border-[#1a1a1a] shadow-2xl overflow-hidden group">
                            <div className="absolute top-0 inset-x-0 h-10 flex items-center justify-center z-10">
                                <div className="w-20 h-5 bg-black rounded-b-[18px]" />
                            </div>

                            {/* CONTENIDO TELÉFONO */}
                            <div className="absolute inset-0 pt-16 px-6 z-0">
                                <div className="w-10 h-10 bg-primary-orange/20 rounded-xl flex items-center justify-center mb-6">
                                    <Ticket className="text-primary-orange" size={20} />
                                </div>
                                <div className="space-y-4">
                                    <div className="h-6 w-3/4 bg-white/10 rounded-lg" />
                                    <div className="h-4 w-full bg-white/5 rounded-md" />
                                    <div className="h-4 w-5/6 bg-white/5 rounded-md" />
                                </div>
                            </div>

                            {/* TICKET POPUP (ANIMACIÓN) */}
                            <div className="absolute inset-x-4 bottom-0 transform translate-y-full group-hover:translate-y-[-16px] transition-transform duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)] z-10 bg-white rounded-t-[32px] rounded-b-[24px] overflow-hidden shadow-[0_-20px_40px_rgba(0,0,0,0.5)] flex flex-col items-center pt-8 pb-4">
                                {/* Zona central "QR" mock */}
                                <div className="w-48 h-56 border-2 border-dashed border-gray-200 rounded-[20px] bg-gradient-to-b from-gray-50 to-white flex items-center justify-center relative shadow-inner mb-6">
                                    <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(68,68,68,.1)_50%,transparent_75%,transparent_100%)] bg-[length:20px_20px] opacity-10" />
                                </div>
                                <div className="w-48 h-8 bg-[#0B0F19] rounded-2xl mb-4" />
                                <p className="text-[10px] font-black uppercase text-gray-300 tracking-[4px]">VALID TICKET</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="py-20 bg-white/[0.02] border-y border-white/5 relative z-10 overflow-hidden text-center grid grid-cols-2 md:grid-cols-4 gap-12 px-6">
                <Stat value="+500k" label="Tickets Vendidos" />
                <Stat value="100%" label="Pagos Seguros" />
                <Stat value="+1k" label="Organizadores" />
                <Stat value="0.5s" label="Acreditación" />
            </section>

            {/* PRICING PLANS */}
            <section className="py-20 px-6 relative z-10 mx-auto max-w-7xl">
                <div className="text-center mb-16">
                    <h2 className="text-4xl sm:text-5xl font-black tracking-tight mb-4">Un modelo transparente.</h2>
                    <p className="text-white/40 text-lg font-medium max-w-xl mx-auto">Dos planes diseñados específicamente para escalar tu negocio sin límites.</p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {/* Plan Salones */}
                    <div className="bg-[#121212] border border-white/5 hover:border-white/10 rounded-[40px] overflow-hidden group transition-all flex flex-col">
                        <div className="aspect-[16/9] w-full overflow-hidden relative">
                            <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent z-10" />
                            <img src="/images/pricing-salon.png" alt="Salón de Fiestas" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                            <div className="absolute top-4 left-4 z-20">
                                <span className="bg-primary-orange text-black px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">Salones Fijos</span>
                            </div>
                        </div>
                        <div className="p-8 lg:p-10 flex-1 flex flex-col">
                            <h3 className="text-3xl font-black mb-2">Suscripción Mensual</h3>
                            <div className="flex items-baseline gap-2 mb-3">
                                <span className="text-4xl font-black text-primary-orange">$35.000</span>
                                <span className="text-xs font-bold text-white/40 uppercase tracking-widest">ARS / mes</span>
                            </div>
                            <div className="mb-6 inline-block w-fit bg-primary-orange/10 border border-primary-orange/20 text-primary-orange text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-[0_0_15px_rgba(255,107,0,0.1)]">
                                🎁 ¡Los primeros 3 meses son gratis!
                            </div>
                            <p className="text-xl font-bold text-white/50 mb-6">Uso ilimitado de la plataforma.</p>
                            <p className="text-white/40 mb-8 font-medium leading-relaxed">Con nuestro plan para salones y complejos, pagás una membresía de tarifa plana. Vas a poder crear eventos todas las semanas sin pagar porcentajes de la venta a la plataforma.</p>
                            
                            <ul className="space-y-4 mb-10 text-sm font-bold text-white/70 flex-1">
                                <li className="flex items-center gap-3"><ShieldCheck size={18} className="text-primary-orange" /> Cero comisión extra por venta de tickets</li>
                                <li className="flex items-center gap-3"><ShieldCheck size={18} className="text-primary-orange" /> Eventos ilimitados</li>
                                <li className="flex items-center gap-3"><ShieldCheck size={18} className="text-primary-orange" /> Control total de tus ingresos y panel de RRPP</li>
                            </ul>
                            <button onClick={() => openCreateModal('subscription', 'venue', true)} className="w-full bg-white/5 hover:bg-white/10 border border-white/10 py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all mt-auto">Configurar Salón</button>
                        </div>
                    </div>

                    {/* Plan Productoras */}
                    <div className="bg-[#121212] border border-white/5 hover:border-white/10 rounded-[40px] overflow-hidden group transition-all relative flex flex-col">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 z-0 pointer-events-none" />
                        <div className="aspect-[16/9] w-full overflow-hidden relative z-10">
                            <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent z-10" />
                            <img src="/images/pricing-productora.png" alt="Productoras de Eventos" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                            <div className="absolute top-4 left-4 z-20">
                                <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">Productoras</span>
                            </div>
                        </div>
                        <div className="p-8 lg:p-10 relative z-10 flex-1 flex flex-col">
                            <h3 className="text-3xl font-black mb-2">Comisión Cero</h3>
                            <div className="flex items-baseline gap-2 mb-4">
                                <span className="text-4xl font-black text-blue-400">$0</span>
                                <span className="text-xs font-bold text-white/40 uppercase tracking-widest">Costo de Mantenimiento</span>
                            </div>
                            <p className="text-xl font-bold text-blue-400/50 mb-6">Vos ganás el 100% de tu ticket.</p>
                            <p className="text-white/40 mb-8 font-medium leading-relaxed">Olvidate de los costos fijos. Como productora independiente o de festivales, usás todo el ecosistema de Passar totalmente gratis. El costo por el servicio lo abona directamente el cliente final.</p>
                            
                            <ul className="space-y-4 mb-10 text-sm font-bold text-white/70 flex-1">
                                <li className="flex items-center gap-3"><Zap size={18} className="text-blue-400" /> Sin costos de suscripción ni mantenimientos fijos</li>
                                <li className="flex items-center gap-3"><Zap size={18} className="text-blue-400" /> Liquidez total e inmediata en Mercado Pago</li>
                                <li className="flex items-center gap-3"><Zap size={18} className="text-blue-400" /> Infraestructura robusta para eventos masivos</li>
                            </ul>
                            <button onClick={() => openCreateModal('commission', 'producer', true)} className="w-full bg-brand-gradient py-4 rounded-2xl font-black text-sm text-white uppercase tracking-widest hover:scale-[1.02] transition-transform shadow-[0_0_20px_rgba(255,107,0,0.2)] mt-auto">Registrar Productora</button>
                        </div>
                    </div>

                    {/* Plan Deportes */}
                    <div className="bg-[#121212] border border-white/5 hover:border-white/10 rounded-[40px] overflow-hidden group transition-all flex flex-col">
                        <div className="aspect-[16/9] w-full overflow-hidden relative">
                            <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent z-10" />
                            <img src="/images/pricing-deportes.png" alt="Complejos Deportivos" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                            <div className="absolute top-4 left-4 z-20">
                                <span className="bg-green-500 text-black px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">Deportes</span>
                            </div>
                        </div>
                        <div className="p-8 lg:p-10 flex-1 flex flex-col">
                            <h3 className="text-3xl font-black mb-2">Comisión Cero</h3>
                            <div className="flex items-baseline gap-2 mb-3">
                                <span className="text-4xl font-black text-green-500">$0</span>
                                <span className="text-xs font-bold text-white/40 uppercase tracking-widest">Costo de mantenimiento</span>
                            </div>
                            <div className="mb-6 inline-block w-fit bg-green-500/10 border border-green-500/20 text-green-400 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-[0_0_15px_rgba(34,197,94,0.1)]">
                                ⚽ ¡Cobrá inscripciones y tickets!
                            </div>
                            <p className="text-xl font-bold text-white/50 mb-6">Llevá la gestión a otro nivel.</p>
                            <p className="text-white/40 mb-8 font-medium leading-relaxed">Con nuestro plan para predios deportivos, administrás torneos, venta de entradas y gestión de equipos totalmente gratis. El costo por el servicio lo abona directamente el espectador o jugador.</p>
                            
                            <ul className="space-y-4 mb-10 text-sm font-bold text-white/70 flex-1">
                                <li className="flex items-center gap-3"><Ticket size={18} className="text-green-500" /> Sistema de Torneos automático</li>
                                <li className="flex items-center gap-3"><Ticket size={18} className="text-green-500" /> Venta de entradas a partidos online</li>
                                <li className="flex items-center gap-3"><Ticket size={18} className="text-green-500" /> Cero costo de mantemiento fijo</li>
                            </ul>
                            <button onClick={() => openCreateModal('commission', 'sports', true)} className="w-full bg-white/5 hover:bg-white/10 border border-white/10 py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all mt-auto">Configurar Complejo</button>
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section className="py-32 px-6 relative z-10 mx-auto max-w-7xl">
                <div className="text-center mb-24">
                    <h2 className="text-4xl sm:text-6xl font-black tracking-tight mb-6">Todo lo que necesitás <br /> para que <span className="text-primary-orange underline decoration-primary-orange/30 decoration-8 underline-offset-8">vayan</span> todos.</h2>
                    <p className="text-white/40 text-lg font-medium max-w-xl mx-auto">Diseñado por y para productores de eventos nocturnos y festivales.</p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <FeatureCard
                        icon={<Smartphone size={32} />}
                        title="Venta Online"
                        desc="Que tu público compre en 2 segundos desde el celular, con el medio que prefiera."
                    />
                    <FeatureCard
                        icon={<CreditCard size={32} />}
                        title="Cobros Mercado Pago"
                        desc="Conectá tu cuenta y recibí el dinero al instante. Sin intermediarios ni esperas."
                        accent="LIQUIDEZ"
                    />
                    <FeatureCard
                        icon={<Users size={32} />}
                        title="Gestión de RRPP"
                        desc="Asigná comisiones, links personalizados y controlá a todo tu equipo en tiempo real."
                    />
                    <FeatureCard
                        icon={<Star size={32} />}
                        title="Invitaciones VIP"
                        desc="Enviá cortesías masivas o individuales con seguimiento de ingreso."
                    />
                    <FeatureCard
                        icon={<BarChart3 size={32} />}
                        title="Métricas en Vivo"
                        desc="Conocé quién entró y cuánto vendiste al instante."
                    />
                    <FeatureCard
                        icon={<ShieldCheck size={32} />}
                        title="Seguridad QR"
                        desc="Validación ultra rápida con nuestra app web propia. Evitá el fraude."
                    />
                </div>
            </section>

            <PublicFooter />

            {openCreate && (
                <CreateSalonModal 
                    open={!!openCreate} 
                    onClose={() => setOpenCreate(null)} 
                    onSuccess={handleCreated} 
                    defaultBillingModel={openCreate.billingModel} 
                    defaultOrganizationType={openCreate.organizationType}
                    hideSelectors={openCreate.hideSelectors}
                />
            )}
            <ClaimSalonModal
                open={success.open}
                slug={success.slug}
                claimUrl={success.claimUrl}
                onClose={() => setSuccess((s) => ({ ...s, open: false }))}
                onGoToSalon={() => success.slug && navigate(`/${success.slug}`)}
            />

            {/* SALON PICKER MODAL */}
            {showPicker && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={() => setShowPicker(false)}>
                    <div className="w-full max-w-md bg-[#0A0A0A] border border-white/10 rounded-[40px] overflow-hidden animate-fade-in-up" onClick={e => e.stopPropagation()}>
                        <div className="px-8 pt-8 pb-6 flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-black text-white">Mis Espacios</h2>
                                <p className="text-xs font-bold text-white/30 uppercase tracking-widest">Elegí a dónde querés ir</p>
                            </div>
                            <button onClick={() => setShowPicker(false)} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-all">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="px-8 pb-8 space-y-3">
                            {(Array.isArray(mySalons) ? mySalons : []).map(s => (
                                <button
                                    key={s._id || s.id || s.slug}
                                    onClick={() => { navigate(`/${s.slug}`); setShowPicker(false); }}
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center justify-between group hover:border-primary-orange/50 transition-all"
                                >
                                    <div className="text-left">
                                        <p className="font-black text-white group-hover:text-primary-orange transition-colors">{s.nombre}</p>
                                        <p className="text-[10px] font-bold text-white/20 uppercase tracking-widest">{s.role === 'admin' ? 'Administrador' : 'Cliente'}</p>
                                    </div>
                                    <ChevronRight size={20} className="text-white/20 group-hover:text-primary-orange group-hover:translate-x-1 transition-all" />
                                </button>
                            ))}
                            <div className="pt-2 border-t border-white/10 mt-2 space-y-2">
                                <button
                                    onClick={() => { setShowPicker(false); openCreateModal('subscription', 'venue', false); }}
                                    className="w-full bg-transparent border border-dashed border-white/20 hover:border-blue-400 hover:text-blue-400 rounded-2xl py-3 px-4 flex items-center justify-center gap-2 group transition-all text-white/50 font-bold text-xs"
                                >
                                    <span>+</span> Registrar Salón o Productora
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MOBILE MENU MODAL */}
            {isMobileMenuOpen && (
                <div className="fixed inset-0 z-[100] bg-[#080808] flex flex-col p-6 animate-fade-in-up sm:hidden">
                    <div className="flex items-center justify-between mb-12">
                        <div className="flex items-center gap-2">
                            <div className="w-10 h-10 bg-brand-gradient rounded-xl flex items-center justify-center">
                                <Ticket className="text-white" size={20} />
                            </div>
                            <span className="font-black tracking-tighter text-2xl text-white">PASSAR</span>
                        </div>
                        <button onClick={() => setIsMobileMenuOpen(false)} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-all">
                            <X size={20} />
                        </button>
                    </div>
                    <div className="flex flex-col gap-8 text-2xl font-black">
                        <Link to="/eventos" onClick={() => setIsMobileMenuOpen(false)} className="text-white hover:text-primary-orange transition-colors">Eventos</Link>
                        <a href="/suport" onClick={() => setIsMobileMenuOpen(false)} className="text-white hover:text-primary-orange transition-colors">Soporte</a>
                        {isAuthenticated && (
                            <button
                                onClick={() => { setIsMobileMenuOpen(false); handleLogout(); }}
                                className="text-red-400 text-left hover:text-red-300 transition-colors"
                            >
                                Cerrar Sesión
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const Stat = ({ value, label }) => (
    <div className="group">
        <p className="text-3xl sm:text-4xl font-black mb-1">{value}</p>
        <p className="text-[10px] font-black text-white/30 uppercase tracking-[3px]">{label}</p>
    </div>
);

const FeatureCard = ({ icon, title, desc, accent }) => (
    <div className="bg-glass p-8 rounded-[32px] border border-white/5 hover:border-white/10 transition-all">
        <div className="mb-6 text-primary-orange">{icon}</div>
        <div className="flex items-center gap-3 mb-3">
            <h3 className="text-xl font-black">{title}</h3>
            {accent && <span className="px-2 py-0.5 bg-primary-orange text-black text-[8px] font-black rounded-full">{accent}</span>}
        </div>
        <p className="text-white/40 text-sm font-medium leading-relaxed">{desc}</p>
    </div>
);

export default Organizers;
